import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MessageCircle } from "lucide-react";
import { ChapterHeading, FadeUp, WHATSAPP_URL, FloralCorner } from "./shared";

const FAQS = [
    {
        q: "O que faz uma gerontóloga e como ela pode ajudar meu familiar idoso?",
        a: "A gerontóloga é a profissional especializada no envelhecimento humano. Ela avalia as necessidades físicas, cognitivas e emocionais da pessoa idosa, orienta a família e monta um plano de cuidado individualizado para promover autonomia, dignidade e qualidade de vida em cada fase.",
    },
    {
        q: "Como funciona o atendimento domiciliar em Curitiba e região?",
        a: "O atendimento acontece no conforto do lar, sem deslocamento nem salas de espera. Na primeira visita, eu realizo uma avaliação completa, converso com a família e defino junto com todos a frequência e os objetivos do acompanhamento — sempre respeitando a rotina da pessoa idosa.",
    },
    {
        q: "Quais regiões de Curitiba são atendidas?",
        a: "O atendimento domiciliar cobre Curitiba e a região metropolitana. Entre em contato pelo WhatsApp (41) 99192-6791 para confirmar a disponibilidade no seu bairro e agendar uma visita inicial.",
    },
    {
        q: "Qual a diferença entre uma gerontóloga e uma cuidadora de idosos?",
        a: "A cuidadora executa os cuidados do dia a dia, como higiene e companhia. A gerontóloga é a especialista que avalia, planeja e orienta todo o cuidado: saúde mental, cognição, adaptação da rotina e da casa, e suporte à família e à própria cuidadora. As duas atuações se complementam.",
    },
    {
        q: "Como a Terapia Ocupacional ajuda idosos com perda cognitiva?",
        a: "A Terapia Ocupacional trabalha atividades significativas do cotidiano para estimular memória, coordenação e independência. Em casos de Alzheimer, demências ou depressão, ela ajuda a manter habilidades, reduzir a ansiedade e dar mais segurança e propósito à rotina do idoso.",
    },
    {
        q: "Como agendar uma avaliação inicial?",
        a: "É simples: chame no WhatsApp (41) 99192-6791 ou envie uma mensagem pelo formulário aqui do site. Eu retorno pessoalmente para entender a situação da sua família e combinar o melhor dia e horário para a primeira visita, sem compromisso.",
    },
];

const FaqSection = () => (
    <section id="faq" className="relative overflow-hidden py-20 sm:py-28" data-testid="faq-section">
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-full bg-gradient-to-b from-white via-lav/25 to-cream" aria-hidden="true" />
        <FloralCorner className="pointer-events-none absolute -left-8 top-8 h-44 w-44 text-lilac/30" />
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
            <ChapterHeading number="05" eyebrow="Dúvidas Frequentes" align="center" title={<>Perguntas de quem <span className="italic text-serenity">cuida</span>, respostas de quem entende</>} />

            <FadeUp delay={0.15} className="mt-12">
                <Accordion type="single" collapsible className="space-y-4">
                    {FAQS.map((f, i) => (
                        <AccordionItem
                            key={f.q}
                            value={`faq-${i}`}
                            data-testid={`faq-item-${i + 1}`}
                            className="rounded-3xl border-2 border-lilac/40 bg-white px-6 shadow-md shadow-plum/5 transition-colors duration-300 data-[state=open]:border-lilac sm:px-8"
                        >
                            <AccordionTrigger
                                data-testid={`faq-trigger-${i + 1}`}
                                className="py-6 text-left font-serif text-lg font-semibold text-plum hover:no-underline sm:text-xl [&[data-state=open]>svg]:text-serenity"
                            >
                                {f.q}
                            </AccordionTrigger>
                            <AccordionContent className="pb-6 text-base leading-relaxed text-ink sm:text-lg">
                                {f.a}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </FadeUp>

            <FadeUp delay={0.3} className="mt-10 text-center">
                <p className="text-base text-ink sm:text-lg">Não encontrou sua dúvida?</p>
                <a
                    href={`${WHATSAPP_URL}?text=${encodeURIComponent("Olá, Maricleia! Tenho uma dúvida sobre o atendimento domiciliar.")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="faq-whatsapp-button"
                    className="mt-4 inline-flex items-center gap-2.5 rounded-full bg-gradient-to-r from-lilac via-serenity to-serenitylight px-8 py-4 text-base font-semibold text-white shadow-xl shadow-serenity/30 transition-transform duration-300 hover:scale-[1.03]"
                >
                    <MessageCircle size={19} />
                    Perguntar pelo WhatsApp
                </a>
            </FadeUp>
        </div>
    </section>
);

export default FaqSection;
